import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Avatar,
  Button
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import { getUserFromCookies } from '../../utils/Cookie-utils';
import useStudentService from '../../api/services/studentService';
import DetailsBox from '../../components/DetailsBox';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as image from '../../assets';
import { getImageUrl } from '../../utils/utils';

const ParentsOfStudent = () => {
  const { studentId } = useParams();
  const theme = useTheme();
  const borderColor = theme.palette.mode === 'dark' ? 'white' : 'black';
  const colors = tokens(theme.palette.mode);

  const [studentDetails, setStudentDetails] = useState(null);
  const [assignedParents, setAssignedParents] = useState([]);
  const [loading, setLoading] = useState(true);

  const { loadStudentDetails, getParentsByStudentId } = useStudentService();

  useEffect(() => {
    const fetchData = async () => {
      if (!studentId) return;
      setLoading(true);
      try {
        const studentData = await loadStudentDetails(studentId);
        const parentData = await getParentsByStudentId(studentId);

        setStudentDetails(studentData);
        setAssignedParents(Array.isArray(parentData) ? parentData : []);
      } catch (error) {
        console.error('Error loading parent/student data:', error);
        setAssignedParents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [studentId]);

  if (loading) {
    return <LoadingSpinner message="Loading parent and student data..." />;
  }

  return (
    <Box sx={{ mx: 2 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        border={`1px solid ${borderColor}`}
        mb={2}
        flexWrap="wrap"
      >
        {/* Left */}
        <Box flex={1} display="flex" alignItems="center" justifyContent="center" p={1}>
          <Box display="flex" alignItems="center" flexDirection={{ xs: 'column', md: 'row' }}>
            <Box
              component="img"
              src={image.visigalogo}
              alt="logo"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography variant="h7" fontWeight="bold" sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}>
              St Mary's Junior Seminary (VISIGA SEMINARY)
            </Typography>
          </Box>
        </Box>

        {/* Center */}
        <Box flex={1} display="flex" justifyContent="center" alignItems="center" p={2} textAlign="center">
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' } }}>
              STUDENT'S PARENTS
          </Typography>
        </Box>

        {/* Right */}
        <Box flex={1} display="flex" alignItems="center" justifyContent="center" p={1}>
          <Box display="flex" alignItems="center" flexDirection={{ xs: 'column', md: 'row' }}>
            <Box
              component="img"
              src={image.cathedral5}
              alt="arch"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}>
              Seminary under Archdiocese of Dar es Salaam
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Student Profile */}
      <Box display="flex" alignItems="center" mb={3}>
        <Avatar
          alt="student"
          src={getImageUrl(studentDetails?.photoUrl) || image.defaultProfile}
          sx={{ width: 100, height: 100 }}
        />
        <Box ml={3}>
          <Typography><strong>Name:</strong> {studentDetails?.studentName || 'N/A'}</Typography>
          <Typography><strong>Status:</strong> {studentDetails?.status || 'N/A'}</Typography>
          <Typography><strong>Created At:</strong> {studentDetails?.createdAt ? new Date(studentDetails.createdAt).toLocaleDateString() : 'N/A'}</Typography>
        </Box>
      </Box>

      {/* Assigned Parents */}
      <Typography 
        variant="h5" 
        mb={2}
        color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
        sx={{ fontWeight: 600 }}
      >
        Assigned Parents
      </Typography>
      {assignedParents.length === 0 ? (
        <Typography 
          color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]}
          sx={{ p: 2, textAlign: 'center' }}
        >
          No parents assigned to this student
        </Typography>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
          gap="20px"
        >
          {assignedParents.map((parent, index) => (
            <Box
              key={parent.id || parent.studentParentId || index}
              sx={{
                backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
                border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[300]}`,
                borderRadius: "8px",
                p: 2,
                boxShadow: theme.palette.mode === "dark" 
                  ? "0 2px 8px rgba(0,0,0,0.2)" 
                  : "0 2px 8px rgba(0,0,0,0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.palette.mode === "dark" 
                    ? "0 8px 16px rgba(0,0,0,0.3)" 
                    : "0 8px 16px rgba(0,0,0,0.15)",
                },
              }}
            >
              <DetailsBox
                name1="PARENT"
                clazz={parent.parentName || 'N/A'}
                class_createdAt={parent.createdAt ? (parent.createdAt.includes('T') ? new Date(parent.createdAt).toLocaleDateString() : parent.createdAt) : 'N/A'}
                class_status={parent.status || 'ACTIVE'}
                name2="CONTACT"
                subject={parent.email || 'N/A'}
                subject_createdAt={parent.phoneNumber ? `Phone: ${parent.phoneNumber}` : 'N/A'}
                subject_status=""
                icon=""
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ParentsOfStudent;
